import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { medications, reviews } from '../services/api';

// CSS styles for HTML content rendering
const htmlContentStyles = `
  .content-display h2, .content-display h3 { 
    font-weight: bold; 
    margin: 16px 0 8px 0; 
  }
  .content-display p { 
    margin: 8px 0; 
    line-height: 1.6; 
  }
  .content-display ul, .content-display ol { 
    margin: 8px 0; 
    padding-left: 24px; 
  }
  .content-display li { 
    margin: 4px 0; 
  }
  .content-display table { 
    border-collapse: collapse; 
    margin: 16px 0; 
    width: 100%; 
  }
  .content-display th, .content-display td { 
    border: 1px solid #ccc; 
    padding: 8px; 
    text-align: left; 
  }
  .content-display th { 
    background: #f5f5f5; 
    font-weight: bold; 
  }
  .content-display .boxed-warning, .content-display [style*="border-left"] { 
    border-left: 4px solid #c00; 
    padding-left: 12px; 
    margin: 16px 0; 
  }
`;

// ALL SECTIONS - keys match what's stored in DB
const SECTIONS = [
  { key: 'indications', label: 'Indications' },
  { key: 'mechanism', label: 'Mechanism of Action' },
  { key: 'dosage_administration', label: 'Dosage & Administration' },
  { key: 'warnings', label: 'Warnings & Precautions' },
  { key: 'adverse_reactions', label: 'Adverse Reactions' },
  { key: 'contraindications', label: 'Contraindications' },
  { key: 'drug_abuse', label: 'Drug Abuse & Dependence' },
  { key: 'interactions', label: 'Drug Interactions' },
  { key: 'overdosage', label: 'Overdosage' },
  { key: 'clinical_pharmacology', label: 'Clinical Pharmacology' },
  { key: 'clinical_studies', label: 'Clinical Studies' },
  { key: 'description', label: 'Description' },
  { key: 'drug_class', label: 'Drug Class' },
  { key: 'nonclinical_toxicology', label: 'Nonclinical Toxicology' },
  { key: 'references', label: 'References' },
  { key: 'dosage_forms', label: 'Dosage Forms & Storage' },
  { key: 'how_supplied', label: 'How Supplied/Storage' },
  { key: 'patient_counseling', label: 'Patient Counseling' },
  { key: 'pregnancy', label: 'Pregnancy' },
  { key: 'lactation', label: 'Lactation' },
  { key: 'pediatric', label: 'Pediatric Use' },
  { key: 'geriatric', label: 'Geriatric Use' },
  { key: 'renal_impairment', label: 'Renal Impairment' },
  { key: 'hepatic_impairment', label: 'Hepatic Impairment' },
  { key: 'females_males_reproductive', label: 'Reproductive Potential' },
  { key: 'immunogenicity', label: 'Immunogenicity' },
  { key: 'pharmacokinetics', label: 'Pharmacokinetics' },
  { key: 'pharmacodynamics', label: 'Pharmacodynamics' },
  { key: 'pharmacogenomics', label: 'Pharmacogenomics' },
  { key: 'microbiology', label: 'Microbiology' },
  { key: 'black_box_warning', label: 'Black Box Warning' }
];

// Map frontend keys to backend API keys (currently not needed - keys match)
const apiKeyMap = {
  // Legacy mapping kept for reference - keys now match between frontend and backend
  // 'indications': 'indications',
  // 'dosage_administration': 'dosage_administration',
  // 'adverse_reactions': 'adverse_reactions',
  // 'pregnancy': 'pregnancy'
};

// Convert frontend key to API key - now just returns the key as-is
const toApiKey = (key) => key;

function ReviewInterface() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [medication, setMedication] = useState(null);
  const [review, setReview] = useState(null);
  const [currentSection, setCurrentSection] = useState('indications');
  const [editedContent, setEditedContent] = useState({});
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showMasterModal, setShowMasterModal] = useState(false);
  
  // Timer still runs in background but won't be displayed
  const startTimeRef = useRef(Date.now());
  const timerIntervalRef = useRef(null);

  // Inject HTML content styles
  useEffect(() => {
    const styleId = 'review-interface-html-styles';
    if (!document.getElementById(styleId)) {
      const styleEl = document.createElement('style');
      styleEl.id = styleId;
      styleEl.textContent = htmlContentStyles;
      document.head.appendChild(styleEl);
    }
    return () => {
      const styleEl = document.getElementById(styleId);
      if (styleEl) {
        styleEl.remove();
      }
    };
  }, []);

  useEffect(() => {
    const loadData = async () => {
      await fetchData();
    };
    
    loadData();
    
    // Adjust scroll areas to account for fixed footer
    const adjustScrollAreas = () => {
      console.log('Running adjustScrollAreas...');
      const footer = document.querySelector('.review-footer');
      const sidebar = document.querySelector('.review-sidebar');
      const contentPanels = document.querySelector('.content-panels');
      
      console.log('Found elements:', { footer: !!footer, sidebar: !!sidebar, panels: !!contentPanels });
      
      if (footer && (sidebar || contentPanels)) {
        const footerHeight = footer.offsetHeight || 70;
        const viewportHeight = window.innerHeight;
        const headerHeight = 80;
        const availableHeight = viewportHeight - headerHeight - footerHeight + 100;
        
        console.log('Calculated height:', availableHeight);
        
        if (sidebar) {
          sidebar.style.setProperty('height', availableHeight + 'px', 'important');
          sidebar.style.setProperty('overflow-y', 'auto', 'important');
          console.log('Sidebar height set to:', sidebar.style.height);
        }
        if (contentPanels) {
          contentPanels.style.setProperty('height', availableHeight + 'px', 'important');
          contentPanels.style.setProperty('overflow-y', 'auto', 'important');
          console.log('Panels height set to:', contentPanels.style.height);
        }
      }
    };
    
    // Run on load and after a short delay
    setTimeout(adjustScrollAreas, 100);
    window.addEventListener('resize', adjustScrollAreas);
    
    // Start timer (background only - no UI display)
    timerIntervalRef.current = setInterval(() => {
      // Time tracking continues but we don't show it
      // Could send to backend periodically if needed
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      console.log('Time elapsed:', elapsed, 'seconds'); // For debugging/backend sync
    }, 1000);

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      window.removeEventListener('resize', adjustScrollAreas);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch medication details
      const medResponse = await medications.get(id);
      setMedication(medResponse.data);
      
      // Initialize edited content with current medication data
      const content = {};
      const aiMeta = medResponse.data.ai_metadata || {};
      const simplifiedData = aiMeta.simplified_content || {};
      SECTIONS.forEach(section => {
        // Pharmacists edit SIMPLIFIED content only
        const simplifiedField = `${section.key}_simplified`;
        const value = medResponse.data[simplifiedField] || simplifiedData[section.key] || '';
        content[section.key] = typeof value === 'object'
          ? JSON.stringify(value, null, 2)
          : value;
      });
      setEditedContent(content);
      
      // Fetch review status - get current user's active claim for this medication
      try {
        const myClaimsResponse = await medications.myClaims();
        const claims = myClaimsResponse.data;
        const claim = claims.find(c => c.medication_id === parseInt(id));
        
        if (claim && claim.review_id) {
          const reviewResponse = await reviews.get(claim.review_id);
          setReview(reviewResponse.data);
        } else {
          // Initialize empty review state
          setReview({
            id: null,
            section_approvals: {},
            section_notes: {}
          });
        }
      } catch (err) {
        console.error('Failed to fetch review:', err);
        // Initialize empty review state
        setReview({
          id: null,
          section_approvals: {},
          section_notes: {}
        });
      }
      
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError(err.response?.data?.detail || 'Failed to load medication data');
    } finally {
      setLoading(false);
    }
  };

  const getSectionStatus = (sectionKey) => {
    if (!review || !review.section_approvals) return 'pending';
    
    const approved = review.section_approvals[sectionKey];
    if (approved === true) return 'approved';
    if (approved === false) return 'rejected';
    return 'pending';
  };

  const getSectionIcon = (sectionKey) => {
    const status = getSectionStatus(sectionKey);
    switch (status) {
      case 'approved': return '✅';
      case 'rejected': return '❌';
      default: return '⏳';
    }
  };

  const hasContent = (sectionKey) => {
    if (!medication) return false;
    const detailedValue = medication[sectionKey];
    const simplifiedValue = medication[`${sectionKey}_simplified`];
    const value = detailedValue || simplifiedValue;
    if (!value) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    if (typeof value === 'object' && Object.keys(value).length === 0) return false;
    return true;
  };

  const handleSectionClick = (sectionKey) => {
    setCurrentSection(sectionKey);
    setNotes(review?.section_notes?.[sectionKey] || '');
  };

  const handleContentChange = (value) => {
    setEditedContent({
      ...editedContent,
      [currentSection]: value
    });
  };

  const saveContent = async () => {
    try {
      setSaving(true);
      
      // Prepare update payload
      const updateData = {};
      updateData[currentSection] = editedContent[currentSection];
      
      await medications.update(id, updateData);
      
    } catch (err) {
      console.error('Failed to save content:', err);
      alert(err.response?.data?.detail || 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    try {
      setSaving(true);
      
      // Save content first
      await saveContent();
      
      // Approve section
      if (!review || !review.id) {
        alert('Review not found. Please try reloading the page.');
        return;
      }
      
      await reviews.approveSection(review.id, toApiKey(currentSection), true, notes);
      
      // Update local state
      setReview({
        ...review,
        section_approvals: {
          ...review.section_approvals,
          [currentSection]: true
        },
        section_notes: {
          ...review.section_notes,
          [currentSection]: notes
        }
      });
      
      // Clear notes
      setNotes('');
      
      // Move to next section
      const currentIndex = SECTIONS.findIndex(s => s.key === currentSection);
      if (currentIndex < SECTIONS.length - 1) {
        const nextSection = SECTIONS[currentIndex + 1];
        setCurrentSection(nextSection.key);
        setNotes(review?.section_notes?.[nextSection.key] || '');
      }
      
    } catch (err) {
      console.error('Failed to approve section:', err);
      alert(err.response?.data?.detail || 'Failed to approve section');
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!notes.trim()) {
      alert('Please provide notes explaining why you are rejecting this section.');
      return;
    }

    try {
      setSaving(true);
      
      // Reject section
      if (!review || !review.id) {
        alert('Review not found. Please try reloading the page.');
        return;
      }
      
      await reviews.approveSection(review.id, toApiKey(currentSection), false, notes);
      
      // Update local state
      setReview({
        ...review,
        section_approvals: {
          ...review.section_approvals,
          [currentSection]: false
        },
        section_notes: {
          ...review.section_notes,
          [currentSection]: notes
        }
      });
      
      alert('Section rejected. Please make necessary revisions before re-approving.');
      
    } catch (err) {
      console.error('Failed to reject section:', err);
      alert(err.response?.data?.detail || 'Failed to reject section');
    } finally {
      setSaving(false);
    }
  };

  const areAllSectionsApproved = () => {
    if (!review || !review.section_approvals) return false;
    
    return SECTIONS.every(section => 
      review.section_approvals[section.key] === true
    );
  };

  const handleMasterApprove = () => {
    if (!areAllSectionsApproved()) {
      alert('All sections must be approved before final approval.');
      return;
    }
    
    setShowMasterModal(true);
  };

  const confirmMasterApproval = async () => {
    try {
      setSaving(true);
      
      if (!review || !review.id) {
        alert('Review not found. Please try reloading the page.');
        return;
      }
      
      await reviews.finalApprove(review.id);
      
      alert('Medication published successfully! 🎉');
      navigate('/dashboard/medications');
      
    } catch (err) {
      console.error('Failed to final approve:', err);
      alert(err.response?.data?.detail || 'Failed to publish medication');
    } finally {
      setSaving(false);
      setShowMasterModal(false);
    }
  };

  const renderContentValue = (key, value) => {
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    if (!value) return '(No content)';
    
    // Detect HTML content
    const isHTML = /<[a-z][\s\S]*>/i.test(value);
    if (isHTML) {
      return <div dangerouslySetInnerHTML={{ __html: value }} />;
    }
    return value;
  };

  const getCurrentDetailedValue = () => {
    if (!medication) return '';
    // Try _original version first for HTML content
    const originalKey = currentSection + '_original';
    return medication[originalKey] || medication[currentSection] || medication.ai_metadata?.original_content?.[currentSection] || '';
  };

  const getCurrentSimplifiedValue = () => {
    if (!medication) return '';
    return editedContent[currentSection] || medication[currentSection + '_simplified'] || medication.ai_metadata?.simplified_content?.[currentSection] || '';
  };

  const getSimplificationState = () => {
    const detailed = (getCurrentDetailedValue() || '').trim();
    const simplified = (getCurrentSimplifiedValue() || '').trim();
    if (!detailed && !simplified) return 'empty';
    if (!simplified) return 'missing';
    if (detailed === simplified) return 'unchanged';
    return 'changed';
  };

  if (loading) {
    return <div className="loading">Loading medication...</div>;
  }

  if (error) {
    return (
      <div className="review-interface">
        <div className="error-message">{error}</div>
        <button onClick={() => navigate('/dashboard/medications')} className="btn-primary">
          Back to Medications
        </button>
      </div>
    );
  }

  if (!medication) {
    return <div className="loading">Medication not found</div>;
  }

  return (
    <div className="review-interface">
      <div className="review-header">
        <h2>{medication.name}</h2>
        <div className="review-status">
          {medication.generic_name && (
            <span>Generic: {medication.generic_name}</span>
          )}
          <span className={`status-badge status-${medication.status}`}>
            {medication.status.replace('_', ' ')}
          </span>
        </div>
      </div>

      <div className="review-body">
        <div className="review-sidebar">
          <h3>Sections</h3>
          <ul className="review-sections">
            {SECTIONS.map(section => {
              const isEmpty = !hasContent(section.key);
              return (
                <li
                  key={section.key}
                  className={`section-item ${currentSection === section.key ? 'active' : ''} ${isEmpty ? 'section-empty' : ''}`}
                  onClick={() => handleSectionClick(section.key)}
                >
                  <span className="section-icon">{getSectionIcon(section.key)}</span>
                  <span className="section-name">
                    {section.label}
                    {isEmpty && <span className="no-data-label"> (No data)</span>}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="review-main">
          <div className="content-panels side-by-side">
            <div className="content-panel original">
              <h4>📄 Original FDA Label</h4>
              <div className="content-display">
                {renderContentValue(currentSection, getCurrentDetailedValue() || '(No original content)')}
              </div>
              {medication.fda_label_url && (
                <p style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                  Source: <a href={medication.fda_label_url} target="_blank" rel="noopener noreferrer">
                    FDA Label
                  </a>
                </p>
              )}
            </div>

            <div className="content-panel simplified">
              <h4>✏️ Simplified Content (Editable)</h4>
              <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: 600, color: getSimplificationState() === 'changed' ? '#2e7d32' : getSimplificationState() === 'unchanged' ? '#ef6c00' : '#666' }}>
                {getSimplificationState() === 'changed' && 'Simplified content differs from original for this section.'}
                {getSimplificationState() === 'unchanged' && 'Simplified content currently matches the original for this section.'}
                {getSimplificationState() === 'missing' && 'No simplified content found yet for this section.'}
                {getSimplificationState() === 'empty' && 'No content available for this section.'}
              </div>
              <textarea
                className="content-textarea"
                value={getCurrentSimplifiedValue()}
                onChange={(e) => handleContentChange(e.target.value)}
                placeholder="Edit the simplified content here..."
              />
              <button 
                onClick={saveContent} 
                disabled={saving}
                className="btn-secondary btn-small"
                style={{ marginTop: '8px' }}
              >
                {saving ? 'Saving...' : '💾 Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="review-footer">
        <div className="footer-left">
          <button onClick={() => navigate('/dashboard/my-claim')} className="btn-back">
            ⬅️ Back
          </button>
          <button
            className="btn-approve"
            onClick={handleApprove}
            disabled={saving}
          >
            ✅ Approve Section
          </button>
          <button
            className="btn-reject"
            onClick={handleReject}
            disabled={saving}
          >
            ❌ Reject Section
          </button>
        </div>
        <div className="footer-center">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (optional)..."
            style={{ 
              padding: '8px 12px', 
              border: '1px solid #ddd', 
              borderRadius: '4px',
              fontSize: '14px',
              width: '400px',
              height: '60px',
              resize: 'none',
              fontFamily: 'inherit'
            }}
          />
        </div>
        <div className="footer-right">
          <button
            className="btn-master-approve"
            onClick={handleMasterApprove}
            disabled={!areAllSectionsApproved() || saving}
          >
            🎯 Master Approve
          </button>
        </div>
      </div>

      {showMasterModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>🎯 Final Approval</h3>
            <p>You're about to publish this medication to SimpleMeds.com.</p>
            <p><strong>This action will:</strong></p>
            <ul>
              <li>Publish content immediately to WordPress</li>
              <li>Attribute review to you</li>
              <li>Release this medication from your claims</li>
            </ul>
            <p style={{ marginTop: '16px', color: '#dc2626' }}>
              <strong>Are you sure you want to proceed?</strong>
            </p>
            <div className="modal-actions">
              <button 
                className="btn-secondary"
                onClick={() => setShowMasterModal(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={confirmMasterApproval}
                disabled={saving}
              >
                {saving ? 'Publishing...' : 'Confirm & Publish'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReviewInterface;
